import { useEffect, useMemo, useCallback } from 'react';
import type { DdqSessionQuestion } from '@/types/api.types';
import { useCockpitStore } from '@/stores/useCockpitStore';
import { QueueRail } from './QueueRail';
import { EvidencePane } from './EvidencePane';
import { AutoAcceptBar } from './AutoAcceptBar';
import { useCockpitKeyboard } from '../hooks/useCockpitKeyboard';

export interface ReviewCockpitProps {
  questions: DdqSessionQuestion[];
  sessionId: string | null;
  editingQuestionId: string | null;
  editingResponse: string;
  keyboardEnabled?: boolean;
  onAccept: (questionId: string) => void;
  onReject: (questionId: string) => void;
  onStartEdit: (questionId: string, response: string) => void;
  onSaveEdit: (questionId: string) => void;
  onCancelEdit: () => void;
  onUpdateEditingResponse: (response: string) => void;
  onBatchAccept: (questionIds: string[]) => void;
  isBatchProcessing?: boolean;
}

/**
 * The two-pane review cockpit: a persistent queue (left) drives a persistent
 * evidence pane (right). Replaces the legacy expand/collapse list — the
 * reviewer moves a cursor and acts via always-visible controls or the keyboard,
 * with a batch lane for the safe vault-grounded answers.
 */
export function ReviewCockpit({
  questions,
  sessionId,
  editingQuestionId,
  editingResponse,
  keyboardEnabled = true,
  onAccept,
  onReject,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onUpdateEditingResponse,
  onBatchAccept,
  isBatchProcessing,
}: ReviewCockpitProps) {
  const activeQuestionId = useCockpitStore((s) => s.activeQuestionId);
  const setActiveQuestion = useCockpitStore((s) => s.setActiveQuestion);

  const orderedIds = useMemo(() => questions.map((q) => q.question_id), [questions]);

  // Keep a valid cursor: default to the first question, and recover if the
  // active question is filtered out of the current list.
  useEffect(() => {
    if (questions.length === 0) return;
    if (!activeQuestionId || !orderedIds.includes(activeQuestionId)) {
      setActiveQuestion(orderedIds[0]);
    }
  }, [questions.length, activeQuestionId, orderedIds, setActiveQuestion]);

  const activeQuestion = useMemo(
    () => questions.find((q) => q.question_id === activeQuestionId) ?? null,
    [questions, activeQuestionId],
  );

  const handleEdit = useCallback(
    (questionId: string) => {
      const q = questions.find((item) => item.question_id === questionId);
      if (q) onStartEdit(questionId, q.suggested_response);
    },
    [questions, onStartEdit],
  );

  useCockpitKeyboard({
    enabled: keyboardEnabled && editingQuestionId === null,
    orderedIds,
    activeId: activeQuestionId,
    onNavigate: setActiveQuestion,
    onAccept,
    onEdit: handleEdit,
    onReject,
  });

  return (
    <div className="space-y-4">
      <AutoAcceptBar
        questions={questions}
        onAcceptAll={onBatchAccept}
        isProcessing={isBatchProcessing}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 items-start">
        <QueueRail
          questions={questions}
          activeId={activeQuestionId}
          onSelect={setActiveQuestion}
          className="lg:sticky lg:top-4 max-h-[calc(100vh-8rem)] overflow-y-auto"
        />
        <EvidencePane
          question={activeQuestion}
          sessionId={sessionId}
          isEditing={editingQuestionId !== null && editingQuestionId === activeQuestionId}
          editingResponse={editingResponse}
          onAccept={() => activeQuestion && onAccept(activeQuestion.question_id)}
          onReject={() => activeQuestion && onReject(activeQuestion.question_id)}
          onStartEdit={() => activeQuestion && handleEdit(activeQuestion.question_id)}
          onSaveEdit={() => activeQuestion && onSaveEdit(activeQuestion.question_id)}
          onCancelEdit={onCancelEdit}
          onUpdateEditingResponse={onUpdateEditingResponse}
        />
      </div>
    </div>
  );
}
